import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ITableConfig } from 'projects/common/src/app/interfaces/table/table-config';
import { ITableDataParams } from 'projects/common/src/app/interfaces/table/table-data-params';
import { ITableHeaderParams } from 'projects/common/src/app/interfaces/table/table-header-params';
import { ITableLargeColumnConfig } from 'projects/common/src/app/interfaces/table/table-large-column-config';
import { TableConfigService } from 'projects/common/src/app/services/table/table-config.service';
import { ExcelExportService } from 'projects/common/src/app/services/utils/excel-export.service';
import { excelExportDateFormat } from 'projects/common/src/app/services/utils/util.service';
import { IFilterConfig } from 'src/app/modules/core/interfaces/filter-config';
import { IOutputFilter } from 'src/app/modules/core/interfaces/output-filter';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { ILayoutConfig } from 'src/app/modules/layout/interfaces/layout-config';
import { LayoutConfigService } from 'src/app/modules/layout/services/layout-config.service';
import { IZoneHistoryReportResponse } from '../../interfaces/zone-history';
import { ReportZoneHistoryService } from '../../services/report-zone-history';

@Component({
  selector: 'app-report-zone-history',
  templateUrl: './report-zone-history.component.html',
  styleUrls: ['./report-zone-history.component.scss'],
})
export class ReportZoneHistoryComponent implements OnInit {
  public selectedFilters: IOutputFilter;
  public params: any;
  public tableConfig: ITableConfig;
  public tableData: IZoneHistoryReportResponse[];
  public filterConfig: IFilterConfig;
  public btnSpinner: boolean;
  public disabledExcelBtn: boolean;

  constructor(
    private layoutConfigService: LayoutConfigService,
    private zoneHistoryService: ReportZoneHistoryService,
    private tableService: TableConfigService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _excelExportService: ExcelExportService,
    protected cdr: ChangeDetectorRef,
  ) {
    this.filterConfig = {
      dates: { required: true },
      cleanButton: true,
    };
    this.tableData = [];
    this.disabledExcelBtn = false;
  }

  ngOnInit(): void {
    const config: ILayoutConfig = {
      title: 'reports',
      subtitle: 'report-zone-history',
      imageKey: 'report',
      imageType: 'section',
    };

    this.layoutConfigService.setConfig(config);
    this.cdr.detectChanges();
  }

  /**
   * Function to get report data from received filters
   *
   * @param filters Filters selected to get data
   */
  public async getReportByFilters(selectedFilters: IOutputFilter): Promise<void> {
    this.filterConfig.isLoading = true;
    this.selectedFilters = selectedFilters;
    this.tableData = [];
    this.params = this.zoneHistoryService.setParams(selectedFilters);
    await this.zoneHistoryService
      .getZoneHistoryData(this.params)
      .then((resp) => {
        for (const element of resp) {
          this.tableData.push({
            sector: element.sector ? element.sector : null,
            zone: element.zone ? element.zone : null,
            zoneType: element.zoneType ? element.zoneType : null,
            zoneOldTagId: element.zoneOldTagId ? element.zoneOldTagId : null,
            zoneNewTagId: element.zoneNewTagId ? element.zoneNewTagId : null,
            zoneUpdatedAt: element.zoneUpdatedAt ? element.zoneUpdatedAt : null,
            updatedByUser: element.updatedByUser ? element.updatedByUser : null,
            zoneReasonText: element.zoneReasonText ? element.zoneReasonText : null,
          });
        }
      })
      .finally(() => {
        this.filterConfig.isLoading = false;
      });
    this.setTableConfig(this.tableData);
    const filters = {
      reports: 'report-zone-history',
    };
    this.googleAnalyticsService.sendCustomEvent('filter', filters);
  }

  /**
   * set table config
   */
  public setTableConfig(tableData: any[]): void {
    const customLargeColumns: ITableLargeColumnConfig[] = [
      { field: 'sector', isLargeColumn: true },
      { field: 'zone', isLargeColumn: true },
      { field: 'zoneType', isLargeColumn: true },
      { field: 'zoneOldTagId', isLargeColumn: true, flexGrow: 1.7, customPadding: '0px 20px 0px 0px' },
      { field: 'zoneNewTagId', isLargeColumn: true, flexGrow: 1.5, customPadding: '0px 20px 0px 0px' },
      { field: 'zoneUpdatedAt', isLargeColumn: true, flexGrow: 3, customPadding: '0px 0px 0px 0px' },
      { field: 'updatedByUser', isLargeColumn: true, flexGrow: 1.5, customPadding: '0px 0px 0px 0px' },
      { field: 'zoneReasonText', isLargeColumn: true },
    ];
    const headerParams: ITableHeaderParams = {
      data: tableData,
      customLargeColumns,
      sortables: true,
    };

    const datasetParams: ITableDataParams = {
      tableData,
      customLargeColumns,
      dateConfig: [{ field: 'zoneUpdatedAt', format: 'date-time' }],
    };

    const { header, dataset } = this.tableService.setTableMainConfig(headerParams, datasetParams);

    this.tableConfig = { header, dataset };
  }

  /**
   * Function to download report
   */
  public async downloadReport(): Promise<void> {
    const selectRangeDate = {
      startDate: excelExportDateFormat(this.selectedFilters.startDate),
      endDate: excelExportDateFormat(this.selectedFilters.endDate),
    };

    this.btnSpinner = true;
    this.disabledExcelBtn = true;
    this.zoneHistoryService.getZoneHistoryReportExcel(this.selectedFilters).subscribe({
      next: (response) => {
        this._excelExportService.exportAsExcel(response, 'zone-history-report-filename', selectRangeDate, null, false);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastSuccess();
      },
      error: (error) => {
        console.error(error);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastError();
      },
    });
  }
}
